import useUnsavedChanges from "../hooks/useUnsavedChanges";
import useRecoverableDraft from "../hooks/useRecoverableDraft";
import { discardChanges, useConfirm } from "../components/ConfirmProvider";
import {
  Archive,
  Download,
  ExternalLink,
  FileText,
  FolderOpen,
  Plus,
  RefreshCw,
  RotateCcw,
  Save,
} from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { markdownAPI } from "../services/api";
import toast from "react-hot-toast";

const MAX_LENGTH = 300000;

const downloadName = (value) => {
  const name = String(value || "document")
    .replace(/\.pdf$/i, "")
    .trim()
    .replace(/[^\p{L}\p{N}_-]+/gu, "-")
    .replace(/^-+|-+$/g, "");
  return `${name || "document"}.pdf`;
};

const MarkdownPdf = () => {
  const confirm = useConfirm();
  const [markdown, setMarkdown] = useState("");
  const [filename, setFilename] = useState("document");
  const [pdfUrl, setPdfUrl] = useState("");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [previewError, setPreviewError] = useState("");
  const [previewRetry, setPreviewRetry] = useState(0);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [saving, setSaving] = useState(false);
  const [opening, setOpening] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [documentArchived, setDocumentArchived] = useState(false);
  const [filter, setFilter] = useState("actifs");
  const [documentId, setDocumentId] = useState(null);
  const [version, setVersion] = useState(null);
  const [savedSnapshot, setSavedSnapshot] = useState(JSON.stringify({ markdown: "", filename: "document" }));
  const urlRef = useRef("");
  const fileRef = useRef(null);
  const requestRef = useRef(0);
  const snapshot = JSON.stringify({ markdown, filename });
  const dirty = snapshot !== savedSnapshot;
  const busy = saving || opening || archiving;
  const visibleDocuments = documents.filter((item) => Boolean(item.archived) === (filter === "archives"));
  const allowNavigation = useUnsavedChanges(dirty);
  const { clearDraft, draftNotice } = useRecoverableDraft("markdown", { markdown, filename, documentId, version, savedSnapshot }, dirty, (draft) => {
    clearPreview();
    setPreviewRetry((value) => value + 1);
    setMarkdown(draft.markdown);
    setFilename(draft.filename);
    setDocumentId(draft.documentId || null);
    setVersion(draft.version || null);
    setDocumentArchived(false);
    setSavedSnapshot(draft.savedSnapshot || JSON.stringify({ markdown: "", filename: "document" }));
    setError("");
  });

  const loadDocuments = async () => {
    setLoading(true);
    setListError("");
    try {
      const response = await markdownAPI.getAll();
      setDocuments(response.data);
    } catch {
      setListError("Impossible de charger les documents enregistrés.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDocuments(); }, []);

  const clearPreview = useCallback(() => {
    requestRef.current += 1;
    setGenerating(false);
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = "";
    }
    setPdfUrl("");
  }, []);

  useEffect(() => {
    clearPreview();
    setPreviewError("");
    if (!markdown.trim()) return;
    setGenerating(true);
    const requestId = requestRef.current;
    const controller = new AbortController();
    let active = true;
    const timer = setTimeout(async () => {
      try {
        const response = await markdownAPI.renderPDF({ markdown, filename }, { signal: controller.signal });
        if (!active || requestId !== requestRef.current) return;
        const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
        urlRef.current = url;
        setPdfUrl(url);
      } catch (requestError) {
        if (!active || requestId !== requestRef.current) return;
        const detail = requestError?.response?.data?.detail;
        setPreviewError(typeof detail === "string" ? detail : "L'aperçu n'a pas pu être généré. Réessaie ou modifie le contenu.");
      } finally {
        if (active && requestId === requestRef.current) setGenerating(false);
      }
    }, 600);
    return () => { active = false; clearTimeout(timer); controller.abort(); };
  }, [markdown, filename, previewRetry, clearPreview]);

  useEffect(() => {
    return () => {
      requestRef.current += 1;
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    };
  }, []);

  const handleMarkdownChange = (event) => {
    clearPreview();
    setError("");
    setMarkdown(event.target.value);
  };

  const handleOpen = async (id) => {
    if (dirty && !await confirm(discardChanges)) return;
    setOpening(true);
    setError("");
    try {
      const { data } = await markdownAPI.getOne(id);
      clearPreview();
      setPreviewRetry((value) => value + 1);
      clearDraft();
      setDocumentId(data.id);
      setVersion(data.version);
      setDocumentArchived(Boolean(data.archived));
      setMarkdown(data.markdown);
      setFilename(data.filename);
      setSavedSnapshot(JSON.stringify({ markdown: data.markdown, filename: data.filename }));
      allowNavigation();
    } catch {
      setError("Impossible d'ouvrir ce document. Votre saisie est conservée.");
    } finally {
      setOpening(false);
    }
  };

  const handleNew = async () => {
    if (dirty && !await confirm(discardChanges)) return;
    clearPreview();
    clearDraft();
    setDocumentId(null);
    setVersion(null);
    setDocumentArchived(false);
    setMarkdown("");
    setFilename("document");
    setSavedSnapshot(JSON.stringify({ markdown: "", filename: "document" }));
    setError("");
    allowNavigation();
  };

  const handleFileImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (markdown.trim() && !await confirm({ title: "Remplacer le document ?", description: "Le fichier importé remplacera le texte présent dans l'éditeur.", confirmLabel: "Importer et remplacer", cancelLabel: "Continuer la saisie" })) { event.target.value = ""; return; }
    if (file.size > MAX_LENGTH * 4) {
      setError("Le fichier est trop volumineux pour être converti.");
      event.target.value = "";
      return;
    }
    let text;
    try {
      text = await file.text();
    } catch {
      setError("Impossible de lire ce fichier Markdown.");
      event.target.value = "";
      return;
    }
    if (text.length > MAX_LENGTH) {
      setError("Le document dépasse la limite de 300 000 caractères.");
      event.target.value = "";
      return;
    }
    clearPreview();
    setError("");
    setMarkdown(text);
    setPreviewRetry((value) => value + 1);
    setFilename(file.name.replace(/\.(md|markdown)$/i, "") || "document");
    event.target.value = "";
  };

  const handleSave = async () => {
    if (!markdown.trim() || busy || documentArchived) return;
    setSaving(true);
    setError("");
    try {
      const payload = { markdown, filename };
      const { data } = documentId
        ? await markdownAPI.update(documentId, { ...payload, expected_version: version })
        : await markdownAPI.create(payload);
      setDocumentId(data.id);
      setVersion(data.version);
      setSavedSnapshot(snapshot);
      setDocuments((current) => [data, ...current.filter((item) => item.id !== data.id)]);
      clearDraft();
      allowNavigation();
      toast.success("Document Markdown enregistré");
    } catch (requestError) {
      const detail = requestError?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "L'enregistrement a échoué. Votre saisie est conservée, réessayez.");
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (item) => {
    if (busy || (item.id === documentId && dirty)) return;
    setArchiving(true);
    setListError("");
    try {
      const { data } = await markdownAPI[item.archived ? "restore" : "archive"](item.id);
      setDocuments((current) => current.map((doc) => doc.id === data.id ? data : doc));
      if (item.id === documentId) {
        setDocumentArchived(Boolean(data.archived));
        setVersion(data.version);
      }
      toast.success(item.archived ? "Document restauré" : "Document archivé");
    } catch (requestError) {
      setListError(requestError?.response?.data?.detail || "Impossible de modifier l'archivage. Réessaie.");
    } finally {
      setArchiving(false);
    }
  };

  const handleDownload = () => {
    if (!pdfUrl || dirty) return;
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = downloadName(filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex items-center gap-3">
        <FileText className="h-8 w-8 text-primary-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Markdown en PDF</h1>
          <p className="text-sm text-gray-600">
            Enregistre ton Markdown pour le retrouver et le modifier, puis télécharge son PDF A4.
          </p>
        </div>
      </div>

      <section className="card space-y-4" aria-label="Documents enregistrés">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-gray-900">Documents enregistrés</h2>
          <button type="button" className="btn-secondary text-sm" onClick={handleNew} disabled={busy}>
            <Plus className="mr-2 h-4 w-4" />Nouveau document
          </button>
        </div>
        <div className="flex gap-2" aria-label="Filtrer les documents">
          {[["actifs", "Actifs"], ["archives", "Archivés"]].map(([key, label]) => (
            <button key={key} type="button" className="btn-secondary text-sm" aria-pressed={filter === key} onClick={() => setFilter(key)}>{label}</button>
          ))}
        </div>
        {listError && (
          <div role="alert" className="text-sm text-red-700">
            <p>{listError}</p>
            <button type="button" className="btn-secondary mt-2" onClick={loadDocuments} disabled={busy}>Réessayer</button>
          </div>
        )}
        {loading ? <p className="text-sm text-gray-500">Chargement des documents...</p> : visibleDocuments.length ? (
          <ul className="divide-y divide-gray-100">
            {visibleDocuments.map((item) => (
              <li key={item.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="break-words font-medium text-gray-900">{item.filename || "document"}</p>
                  <p className="text-xs text-gray-500">
                    Enregistré le {new Date(item.updated_at).toLocaleString("fr-FR", { timeZone: "Europe/Paris" })}
                    {item.id === documentId ? " (document ouvert)" : ""}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" className="btn-secondary text-sm" onClick={() => handleOpen(item.id)} disabled={busy} aria-label={`Ouvrir ${item.filename || "document"}`}>
                    <FolderOpen className="mr-2 h-4 w-4" />{item.archived ? "Consulter" : "Ouvrir et modifier"}
                  </button>
                  <button type="button" className="btn-secondary text-sm" onClick={() => handleArchive(item)} disabled={busy || (item.id === documentId && dirty)} aria-label={`${item.archived ? "Restaurer" : "Archiver"} ${item.filename || "document"}`} title={item.id === documentId && dirty ? "Enregistre les modifications avant d'archiver ce document." : undefined}>
                    {item.archived ? <RotateCcw className="mr-2 h-4 w-4" /> : <Archive className="mr-2 h-4 w-4" />}
                    {item.archived ? "Restaurer" : "Archiver"}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : !listError && <p className="text-sm text-gray-500">{filter === "archives" ? "Aucun document archivé." : "Aucun document enregistré. Le Markdown sera conservé ici après enregistrement."}</p>}
      </section>

      <fieldset disabled={busy}>{draftNotice}</fieldset>
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <section className="card min-w-0 space-y-4" aria-label="Document Markdown">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-gray-900">Document</h2>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="btn-secondary text-sm"
              disabled={busy || documentArchived}
            >
              <FolderOpen className="mr-2 h-4 w-4" />
              Importer un .md
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".md,.markdown,text/markdown,text/plain"
              onChange={handleFileImport}
              disabled={busy || documentArchived}
              className="hidden"
              aria-label="Importer un document Markdown"
            />
          </div>

          <div>
            <label htmlFor="markdown-filename" className="mb-1 block text-sm font-medium text-gray-700">
              Nom du document
            </label>
            <input
              id="markdown-filename"
              className="input-field"
              value={filename}
              maxLength={100}
              onChange={(event) => { clearPreview(); setFilename(event.target.value); setError(""); }}
              disabled={busy || documentArchived}
              placeholder="document"
            />
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between gap-3">
              <label htmlFor="markdown-content" className="text-sm font-medium text-gray-700">
                Contenu Markdown
              </label>
              <span className="text-xs text-gray-500">
                {markdown.length.toLocaleString("fr-FR")} / 300 000
              </span>
            </div>
            <textarea
              id="markdown-content"
              className="input-field min-h-[520px] resize-y font-mono text-sm leading-6"
              value={markdown}
              maxLength={MAX_LENGTH}
              onChange={handleMarkdownChange}
              disabled={busy || documentArchived}
              placeholder={"# Mon document\n\nUn paragraphe avec du **texte en gras**.\n\n- Premier point\n- Deuxième point"}
              spellCheck={false}
            />
          </div>

          {error && (
            <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700">
              {error}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <button type="button" className="btn-primary" onClick={handleSave} disabled={busy || documentArchived || !markdown.trim() || !dirty}>
              <Save className="mr-2 h-4 w-4" />
              {saving ? "Enregistrement..." : "Enregistrer le document"}
            </button>
            <p className="text-xs text-gray-500">
              {documentArchived ? "Document archivé. Restaure-le depuis la liste des archives pour le modifier." : dirty ? "L'aperçu se met à jour automatiquement. Enregistre le document pour télécharger le PDF." : documentId ? "Markdown enregistré dans l'application, disponible pour modification même après téléchargement." : "L'aperçu apparaîtra automatiquement dès la saisie du Markdown."}
            </p>
          </div>
          <p className="text-xs text-gray-500">
            Titres, listes, tableaux, citations, liens et blocs de code sont pris en charge.
            Les images externes sont remplacées par leur légende.
          </p>
        </section>

        <section className="card min-w-0 space-y-4" aria-label="Aperçu PDF">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-gray-900">Aperçu PDF</h2>
            {generating && <p role="status" className="flex items-center text-sm text-gray-500"><RefreshCw className="mr-2 h-4 w-4 animate-spin" />Actualisation de l'aperçu...</p>}
            {pdfUrl && (
              <div className="flex flex-wrap gap-2">
                <a
                  href={pdfUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-secondary text-sm"
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Ouvrir
                </a>
                <button type="button" onClick={handleDownload} className="btn-primary text-sm" disabled={dirty || saving}>
                  <Download className="mr-2 h-4 w-4" />
                  Télécharger
                </button>
              </div>
            )}
          </div>
          {previewError && <div role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700"><p>{previewError}</p><button type="button" className="btn-secondary mt-2 text-sm" onClick={() => setPreviewRetry((value) => value + 1)}>Réessayer l'aperçu</button></div>}
          {pdfUrl ? (
            <iframe
              title="Aperçu du document PDF"
              src={pdfUrl}
              className="h-[740px] w-full rounded-lg border border-gray-200 bg-white"
            />
          ) : (
            <div className="flex h-[740px] items-center justify-center rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 px-6 text-center text-sm text-gray-500">
              {generating ? "Préparation automatique de l'aperçu..." : "Colle ou importe du Markdown pour voir l'aperçu automatiquement."}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default MarkdownPdf;
